import { Link, useNavigate } from "react-router-dom";
import { MapPin, Shield } from "lucide-react";
import { ThemeToggle } from "@/components/theme-toggle";
import { LogoMark } from "@/components/logo";
import { Band, INNER } from "./section";

/**
 * The public top bar and footer, shared by every unauthenticated page. Moved
 * verbatim out of src/pages/Landing.tsx so a second public page can't end up
 * with a second copy of the non-affiliation and crisis copy — the highest-
 * consequence text in the product, and text that has already been revised once.
 */

function LinkButton({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => navigate("/signin")}
      className="rounded-md px-2 py-1 text-sm font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
    >
      {children}
    </button>
  );
}

export function PublicTopBar() {
  return (
    <Band borderBottom>
      <div className={`${INNER} flex items-center justify-between py-4`}>
        {/* The lockup goes home — inert on the landing page, the way back
            from every other public page. */}
        <Link to="/" className="flex items-center gap-2.5">
          <LogoMark size={28} />
          <span className="text-[17px] font-bold text-foreground">Happy24</span>
        </Link>
        <div className="flex items-center gap-1">
          <Link
            to="/literature"
            className="rounded-md px-2 py-1 text-sm font-medium text-muted-foreground hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Literature
          </Link>
          <ThemeToggle />
          <LinkButton>Sign In</LinkButton>
        </div>
      </div>
    </Band>
  );
}

export function PublicFooter() {
  return (
    <Band borderTop>
      <footer className={`${INNER} flex flex-col gap-4 py-8 pb-10`}>
        <div className="flex items-center gap-2.5">
          <LogoMark size={24} />
          <span className="text-[15px] font-bold text-foreground">Happy24</span>
        </div>
        <p className="max-w-[65ch] text-sm leading-[1.6] text-foreground">
          Happy24 is an independent reflection tool. It is not affiliated with,
          endorsed by, or connected to Alcoholics Anonymous, Narcotics Anonymous,
          or any other twelve-step fellowship. Fellowship names are used only so
          you can identify your own literature.
        </p>
        <p className="max-w-[65ch] text-sm leading-[1.6] text-muted-foreground">
          Happy24 is a private reflection tool — not therapy, medical
          treatment, or a crisis service. If you need more than reflection
          right now, call or text{" "}
          <strong className="font-semibold text-foreground">988</strong>, the
          Suicide &amp; Crisis Helpline in the US and Canada. It's free and
          confidential, any hour.
        </p>
        <div className="flex gap-4">
          <Link to="/literature" className="text-[13px] text-muted-foreground underline">
            Which literature
          </Link>
          {/* Privacy and Terms are still unbuilt (ROADMAP). Kept inert rather
              than pointed at "#", which would rewrite the URL on a sub-page. */}
          <span className="text-[13px] text-muted-foreground underline">Privacy</span>
          <span className="text-[13px] text-muted-foreground underline">Terms</span>
        </div>
        <div className="flex flex-col gap-1">
          <p className="flex items-center gap-[7px] text-[13px] text-foreground">
            <Shield className="h-3.5 w-3.5 text-primary" strokeWidth={2} />
            Sober since July 6th, 2021
          </p>
          <p className="flex items-center gap-[7px] text-[13px] text-muted-foreground">
            <MapPin className="h-3.5 w-3.5" strokeWidth={2} />
            Built in Ottawa, Ontario, Canada
          </p>
        </div>
        <p className="text-xs text-muted-foreground">
          © 2026 Happy24 · One day at a time
        </p>
      </footer>
    </Band>
  );
}
